/** Траты или доходы по дням периода — а на годе по месяцам.
 *
 * Столбики, а не линия: между днями нет непрерывности — 12-го потратил, 13-го
 * нет, и соединять их линией значило бы рисовать переход, которого не было.
 *
 * Пустые дни рисуются засечкой, а не пустотой: провал в середине месяца — тоже
 * информация, и убрав его, мы сдвинули бы всю картину.
 *
 * ЧТО БЫЛО НЕ ТАК. Пик красился в `--text` — белая башня оказывалась самым
 * громким пятном экрана, громче самих денег, а остальные дни лежали серой
 * линейкой. Масштаба не было вовсе: понять, «3 240 там или 32», можно было
 * только из подписи под графиком. И цвет не различал доход и расход, хотя всё
 * остальное в приложении их различает.
 */
import type { Side, Summary } from "../lib/api.ts";
import { formatMoney } from "../lib/money.ts";

const HEIGHT = 72;        // 56 было мало: при одном крупном дне остальные слипались
const ZERO_TICK = 2;      // засечка дня без движения денег

export function DailyBars({ summary, side = "expense" }: {
  summary: Summary;
  /** Какой ряд рисуем. Доход и расход в одних столбиках не совместить: они
   *  растут в разные стороны, и общая ось врала бы про масштаб обоих. */
  side?: Side;
}) {
  // На годе сервер склеивает ряд в месяцы: 365 столбиков в ширину телефона —
  // это волоски, по которым нельзя прочесть ничего.
  const monthly = summary.kind === "year";
  const label = (iso: string) => (monthly ? formatMonth(iso) : formatDay(iso));
  // Поле может отсутствовать, если открыт старый бандл против нового API —
  // это штатная рассинхронизация, а не повод гасить экран.
  const days = summary.daily ?? [];
  if (days.length < 2) return null;

  const values = days.map((d) => Math.max(Number(side === "income" ? d.income : d.expense), 0));
  const top = Math.max(...values);
  if (top === 0) return null;

  const peak = values.indexOf(top);
  const peakDay = days[peak]!;
  const ink = side === "income" ? "var(--income)" : "var(--data)";
  const сумма = (i: number) =>
    formatMoney(side === "income" ? days[i]!.income : days[i]!.expense, summary.base_currency);

  return (
    <figure style={{ margin: "var(--s-4) 0 0" }}>
      {/* Линия максимума с числом: она и есть масштаб. Без неё высота столбика
          ничего не значит — «в полтора раза выше» видно, а «выше чего» нет. */}
      <div style={{ display: "flex", alignItems: "center", gap: "var(--s-2)",
                    marginBottom: 4 }}>
        <span className="num" style={{ fontSize: "var(--f-caption-s)",
                                       color: "var(--text-faint)" }}>
          {сумма(peak)}
        </span>
        <span aria-hidden="true"
              style={{ flexGrow: 1, height: 1, background: "var(--separator)" }} />
      </div>

      <div style={{ display: "flex", alignItems: "flex-end", gap: 2, height: HEIGHT }}
           role="img"
           aria-label={`${side === "income" ? "Доходы" : "Траты"}`
                       + ` по ${monthly ? "месяцам" : "дням"}, максимум `
                       + `${сумма(peak)} — ${label(peakDay.day)}`}>
        {values.map((value, i) => (
          <div key={days[i]!.day} title={`${label(days[i]!.day)} · ${сумма(i)}`}
               style={{ flexGrow: 1, minWidth: 2, borderRadius: "2px 2px 0 0",
                        height: value === 0 ? ZERO_TICK : `${Math.max(value / top, 0.05) * 100}%`,
                        background: ink,
                        // Пик в полную силу, остальные приглушены — но тем же
                        // цветом. Отдельный цвет для пика делал бы его событием,
                        // а он просто самый большой из тех же дней.
                        opacity: value === 0 ? 0.25 : i === peak ? 1 : 0.45 }} />
        ))}
      </div>

      {/* Подпись только по краям оси. Пик назван выше числом и цветом — повторять
          его здесь значило бы написать «1 сен» дважды, когда пик пришёлся на
          первый день, что и происходило. */}
      <figcaption className="num"
                  style={{ display: "flex", justifyContent: "space-between",
                           fontSize: "var(--f-caption-s)", color: "var(--text-faint)",
                           marginTop: 6 }}>
        <span>{label(days[0]!.day)}</span>
        <span>{label(days[days.length - 1]!.day)}</span>
      </figcaption>
    </figure>
  );
}

const MONTHS_SHORT = ["янв", "фев", "мар", "апр", "май", "июн",
                      "июл", "авг", "сен", "окт", "ноя", "дек"];

/** «1 сен» вместо «1.09»: в приложении даты пишутся словами, и ось — не место
 *  заводить второй формат ради трёх сэкономленных пикселей. */
function formatDay(iso: string): string {
  const [, month, day] = iso.split("-");
  return `${Number(day)} ${MONTHS_SHORT[Number(month) - 1] ?? month}`;
}

function formatMonth(iso: string): string {
  return MONTHS_SHORT[Number(iso.slice(5, 7)) - 1] ?? iso;
}
